import type { LinkingOptions, PathConfig } from '@react-navigation/native';
import { createURL } from 'expo-linking';
import {
  addNotificationResponseReceivedListener,
  clearLastNotificationResponse,
  getLastNotificationResponse
} from 'expo-notifications';
import { Linking } from 'react-native';

import { authReady } from '@/features/authorization/utils/authState';
import { MEINE_REGION_FEED_ROUTE } from '@/features/geo-area/components/region/regionTypes';
import { MEIN_ORT_FEED_ROUTE } from '@/features/mein-ort/components/meinOrtTypes';
import { getNotificationData } from '@/features/notifications/utils/notificationData';
import { getNotificationLink } from '@/features/notifications/utils/notificationLinks';
import {
  AppRoute,
  TabRoute,
  type AppParam
} from '@/navigation/types/navigation';

const prefix = createURL('/');

const linkedRoutes = Object.values(AppRoute).filter(
  (path) =>
    path !== AppRoute.Tabs &&
    (__DEV__ ||
      (path !== AppRoute.Experiments && path !== AppRoute.FeatureSettings))
);

export const linking: LinkingOptions<AppParam> = {
  enabled: true,
  prefixes: [prefix],
  // NOTE: Once a config exists, RN matches ONLY the declared paths —
  // undeclared top-level screens silently stop deep-linking. Declare every screen;
  config: {
    screens: {
      ...Object.fromEntries(linkedRoutes.map((path) => [path, path])),
      [AppRoute.Tabs]: {
        path: AppRoute.Tabs,
        // The AppParam type is flat (no nested tab navigator), so the
        // PathConfig type omits `screens` here — the runtime supports it.
        screens: {
          [TabRoute.Start]: TabRoute.Start,
          [TabRoute.Radio]: {
            path: TabRoute.Radio,
            screens: { [MEIN_ORT_FEED_ROUTE]: '' }
          },
          [TabRoute.Region]: {
            path: TabRoute.Region,
            screens: { [MEINE_REGION_FEED_ROUTE]: '' }
          },
          [TabRoute.Profile]: TabRoute.Profile
        }
      } as PathConfig<AppParam>
    }
  },
  getInitialURL: async () => {
    // Wait until auth is ready
    await authReady;

    const url = await Linking.getInitialURL();
    if (url) {
      return url;
    }

    // URL from notification
    const response = getLastNotificationResponse();
    if (!response?.notification) return;
    const notificationUrl = getNotificationLink(
      getNotificationData(response.notification)
    );
    if (!notificationUrl) return;

    clearLastNotificationResponse();

    return notificationUrl;
  },
  subscribe: (listener) => {
    const listen = async (url: string) => {
      // Ensure deep link is processed only after auth is ready
      await authReady;
      listener(url);
    };

    const onReceiveURL = ({ url }: { url: string }) => listener(url);

    const eventListenerSubscription = Linking.addEventListener(
      'url',
      onReceiveURL
    );

    const subscription = addNotificationResponseReceivedListener((response) => {
      if (!response?.notification) return;
      const url = getNotificationLink(
        getNotificationData(response.notification)
      );

      if (!url) return;

      listen(url);
      clearLastNotificationResponse();
    });

    return () => {
      eventListenerSubscription.remove();
      subscription.remove();
    };
  }
};
